import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/dist/cjs/components/Button';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import styled from '@emotion/styled';
import React, {
  useEffect,
  useState,
  PropsWithChildren,
  ReactElement
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDevopsState } from '../hook';
import { SubmitErrorMessage } from '../devops-form-view/styled';
import { SanitizedFormattedHtmlMessage } from '@atlassiansox/bxpkit-core';
import {
  useAppDispatch,
  useAppSelector,
  updateLoggedInFormDataSubmitStatus
} from '../../../redux';
import { messages } from '../messages';
type DevopsCarouselType = {
  readonly children: React.ReactNode;
};

export function DevopsCarousel({
  children
}: PropsWithChildren<DevopsCarouselType>) {
  const navigate = useNavigate();
  const currentRoute = useLocation();
  const dispatch = useAppDispatch();
  const { createAnalyticsEvent } = useAnalyticsEvents();
  const isFormValid = useAppSelector(
    ({ loggedInFormValid: { isValid } }) => isValid
  );
  const { bbWorkspaceName, cloudName } = useAppSelector(
    ({ loggedInFormData }) => loggedInFormData
  );
  const submitErrorMessage = useAppSelector(
    ({ sharedFormData: { submitErrorMessage } }) => submitErrorMessage
  );
  const submitStatus = useAppSelector(
    ({ loggedInFormData: { submitStatus } }) => submitStatus
  );
  const { userInfo, bbworkspaces } = useDevopsState();

  const slidePaths = children
    ? //@ts-ignore
      children.props.children
        .map((child: ReactElement) => child?.props?.path)
        .filter((p: ReactElement) => p !== undefined)
    : [];

  const [slideIndex, setSlideIndex] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(
    slideIndex >= slidePaths.length - 1 || !isFormValid
  );
  const [isPrevDisabled, setIsPrevDisabled] = useState(slideIndex === 0);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const isFooterHidden = currentRoute?.pathname === '/confirmation';

  const routeActionConfig: { [key: string]: any } = {
    '/confirmation': {
      submit: true
    },
    '/site-picker': {
      checkKeys: slidePaths.includes('/workspace-picker')
    }
  };

  const nextOnClick = async () => {
    let actionResult;
    // check for any actions, then move to the next slide
    const nextPath = slidePaths[slideIndex + 1];
    // trackEvent of slide progression
    createAnalyticsEvent({
      eventName: 'bxp.formCloudSignup.devops.carouselSlide',
      eventComponent: 'button',
      event: 'clicked',
      data: { route: nextPath }
    }).fire();

    if (routeActionConfig[nextPath]) {
      actionResult = await routeAction(routeActionConfig[nextPath]);
      if (actionResult === 'COMPLETE_SUCCESS') {
        // only proceed to next slide if submit was successful
        setSlideIndex(slideIndex + 1);
      }
      if (actionResult === 'SKIP' && nextPath === '/site-picker') {
        setIsNextDisabled(true);
        setIsNextLoading(true);
        setIsPrevDisabled(true);
        dispatch(updateLoggedInFormDataSubmitStatus('shouldSubmit'));
      }
      if (actionResult === 'CONTINUE' && nextPath === '/site-picker') {
        setSlideIndex(slideIndex + 1);
      }
    } else {
      // no action, proceed to next slide
      setSlideIndex(slideIndex + 1);
    }
  };

  const prevOnClick = async () => {
    let actionResult;
    // check for any actions, then move to the next slide
    const prevPath = slidePaths[slideIndex - 1];
    if (routeActionConfig[prevPath]) {
      actionResult = await routeAction(routeActionConfig[prevPath]);
      if (actionResult === 'SKIP' && prevPath === '/site-picker') {
        setSlideIndex(slideIndex - 2);
      }
      if (actionResult === 'CONTINUE' && prevPath === '/site-picker') {
        setSlideIndex(slideIndex - 1);
      }
      if (actionResult === undefined && prevPath === '/site-picker') {
        setSlideIndex(slideIndex - 1);
      }
    } else {
      // no action, proceed to next slide
      setSlideIndex(slideIndex - 1);
    }

    // TODO analytics
  };
  // decide who gets to be the first slide here
  useEffect(() => {
    if (slidePaths && slidePaths.length > 0) {
      if (userInfo && bbworkspaces) {
        if (
          (userInfo?.sites || []).filter((site) => site?.adminAccess).length ===
            0 &&
          bbworkspaces.length !== 0
        ) {
          navigate(slidePaths[0]);
        }
      }
    }
  }, [userInfo, bbworkspaces]);

  useEffect(() => {
    if (submitStatus === 'success') {
      setIsPrevDisabled(false);
      setIsNextDisabled(false);
      setIsNextLoading(false);

      if (bbWorkspaceName && cloudName && bbWorkspaceName === cloudName) {
        setSlideIndex(slideIndex + 2);
      } else {
        void nextOnClick();
      }
    }
  }, [submitStatus]);

  useEffect(() => {
    if (slidePaths && slidePaths.length > 0) {
      navigate(slidePaths[0]);
    }
  }, []);

  useEffect(() => {
    setIsPrevDisabled(slideIndex === 0);
    navigate(slidePaths[slideIndex]);
  }, [slideIndex]);

  useEffect(() => {
    setIsNextDisabled(!isFormValid);
  }, [isFormValid]);

  async function routeAction(action: {
    [key: string]: any;
  }): Promise<string | void> {
    let result;

    if (action?.submit && submitStatus !== 'success') {
      setIsNextDisabled(true);
      setIsNextLoading(true);
      setIsPrevDisabled(true);

      dispatch(updateLoggedInFormDataSubmitStatus('shouldSubmit'));
    } else if (action?.submit && submitStatus === 'success') {
      result = 'COMPLETE_SUCCESS';
    }

    if (action?.checkKeys) {
      if (bbWorkspaceName && cloudName && bbWorkspaceName === cloudName) {
        return Promise.resolve('SKIP');
      } else {
        return Promise.resolve('CONTINUE');
      }
    }

    return Promise.resolve(result);
  }

  return (
    <Carousel>
      {children}
      <Footer isFooterHidden={isFooterHidden}>
        <Button
          onClick={prevOnClick}
          isDisabled={isPrevDisabled}
          testId="button-devops-carousel-prev"
        >
          <SanitizedFormattedHtmlMessage {...messages.previousButton} />
        </Button>
        <ProgressIndicatorWrapper>
          <ProgressIndicator
            selectedIndex={slideIndex}
            values={slidePaths}
            size={'default'}
            spacing={'comfortable'}
          />
        </ProgressIndicatorWrapper>
        <Button
          onClick={nextOnClick}
          appearance="primary"
          isDisabled={isNextDisabled}
          isLoading={isNextLoading}
          testId="button-devops-carousel-next"
        >
          <SanitizedFormattedHtmlMessage {...messages.nextButton} />
        </Button>
      </Footer>
      {submitErrorMessage && (
        <SubmitErrorMessage>
          <SanitizedFormattedHtmlMessage {...submitErrorMessage} />
        </SubmitErrorMessage>
      )}
    </Carousel>
  );
}
const Footer = styled.div<{ readonly isFooterHidden: boolean }>`
  display: ${(props) => (props.isFooterHidden ? 'none' : 'flex')};
  justify-content: space-around;
  align-items: center;
  margin: 40px auto 0 auto;
  max-width: 312px;
`;

const Carousel = styled.div`
  width: 100%;
  svg {
    margin-bottom: 0px;
  }
`;

const ProgressIndicatorWrapper = styled.div`
  pointer-events: none;
`;
